import { useEffect, useMemo, useState } from 'react'

import { plural } from '../lib/format'
import { useEscClose } from '../lib/useEscClose'
import type { BuildPreview, ClientProduct, CodeOrderDetail } from '../types'

/**
 * Сборка поставки руками — без файла заказа.
 *
 * Файл есть не всегда: поставку заводят и по бумагам от поставщика, и заранее,
 * когда таблицы ещё нет. Вводить при этом пятьсот двадцать номеров паллет
 * человек не должен, и выбирать товар строкой — тоже: названия разойдутся с
 * карточками, и потом не понять, по какому товару заказывать коды.
 *
 * Поэтому здесь ровно три вещи, которые нужно заполнить:
 *   1. номер первой паллеты — один на всю поставку, остальные считает правило;
 *   2. грузовик: номер, номер заказа, адрес;
 *   3. товары в нём — из справочника, с количеством.
 *
 * И одна, которая всё решает: «ещё такой же». В настоящей поставке на двадцать
 * грузовиков разных наборов товаров всего два — остальное повторяется. Набрать
 * один грузовик и размножить его быстрее, чем заполнять двадцать.
 */

export interface BuilderItem {
  /** Своя метка строки: товар можно сменить, а порядок строк — нет */
  key: string
  gtin: string
  per_pack: string
  packs_per_pallet: string
  pallets: string
}

export interface BuilderTruck {
  key: string
  truck_no: string
  order_no: string
  address: string
  items: BuilderItem[]
}

export interface BuildPayload {
  pallet_start: string
  trucks: {
    truck_no: string
    order_no: string
    address: string | null
    items: {
      gtin: string
      per_pack: number | null
      packs_per_pallet: number | null
      pallets: number
    }[]
  }[]
}

let счётчик = 0
const метка = () => `b${++счётчик}`

const пустая_строка = (): BuilderItem => ({
  key: метка(),
  gtin: '',
  per_pack: '',
  packs_per_pallet: '',
  pallets: '',
})

const пустой_грузовик = (): BuilderTruck => ({
  key: метка(),
  truck_no: '',
  order_no: '',
  address: '',
  items: [пустая_строка()],
})

const число = (s: string): number => {
  const n = Number(String(s).replace(',', '.').trim())
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

/** «A-1806» → «A-1807». Номера заказов идут подряд, как и номера грузовиков. */
export function следующий(номер: string): string {
  const м = /^(.*?)(\d+)(\D*)$/.exec(номер.trim())
  if (!м) return номер
  const [, начало, цифры, конец] = м
  return `${начало}${String(Number(цифры) + 1).padStart(цифры.length, '0')}${конец}`
}

/**
 * Открыть конструктор на том, что уже набрано.
 *
 * «Пересобрать» с чистого листа означало бы набрать двадцать грузовиков
 * заново ради одной правки. Номера паллет не переносим: их задаёт правило, а
 * образец берём с первой паллеты — так что при сохранении они получатся те же.
 */
export function из_дерева(order: CodeOrderDetail): {
  start: string
  trucks: BuilderTruck[]
} {
  const первая = order.trucks[0]?.items[0]?.pallets[0]?.number ?? ''
  return {
    start: первая,
    trucks: order.trucks.length
      ? order.trucks.map((t) => ({
          key: метка(),
          truck_no: t.truck_no ?? '',
          order_no: t.order_no ?? '',
          address: t.address ?? '',
          items: t.items.length
            ? t.items.map((i) => ({
                key: метка(),
                gtin: i.gtin ?? '',
                per_pack: i.per_pack ? String(i.per_pack) : '',
                packs_per_pallet: i.packs_per_pallet ? String(i.packs_per_pallet) : '',
                pallets: String(i.pallets.length || i.pallets_declared || 0),
              }))
            : [пустая_строка()],
        }))
      : [пустой_грузовик()],
  }
}

export function в_запрос(pallet_start: string, trucks: BuilderTruck[]): BuildPayload {
  return {
    pallet_start: pallet_start.trim(),
    trucks: trucks.map((t) => ({
      truck_no: t.truck_no.trim(),
      order_no: t.order_no.trim(),
      address: t.address.trim() || null,
      items: t.items
        .filter((i) => i.gtin)
        .map((i) => ({
          gtin: i.gtin,
          per_pack: число(i.per_pack) || null,
          packs_per_pallet: число(i.packs_per_pallet) || null,
          pallets: число(i.pallets),
        })),
    })),
  }
}

export default function SupplyBuilder({
  order,
  products,
  onPreview,
  onSave,
  onClose,
}: {
  /** Поставка, которую собирают. Уже набранное дерево открывается на правку. */
  order: CodeOrderDetail
  products: ClientProduct[]
  onPreview: (body: BuildPayload) => Promise<BuildPreview>
  onSave: (body: BuildPayload) => Promise<CodeOrderDetail>
  onClose: () => void
}) {
  const [начало] = useState(() => из_дерева(order))
  const [start, setStart] = useState(начало.start)
  const [trucks, setTrucks] = useState<BuilderTruck[]>(начало.trucks)
  const [итог, setИтог] = useState<BuildPreview | null>(null)
  /** «считаем», текст ошибки расчёта или пусто */
  const [счёт, setСчёт] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEscClose(onClose)

  const запрос = useMemo(() => в_запрос(start, trucks), [start, trucks])
  const готово = запрос.trucks.some((t) => t.items.length > 0)

  // Итог считает сервер — тем же правилом, которым потом и запишет. Считать
  // его здесь второй раз значит получить две разные арифметики и спор о том,
  // какая верна.
  //
  // Если расчёт не дошёл, это надо сказать вслух. Молчаливый отказ выглядел
  // так: всё заполнено, а «Собрать поставку» не нажимается — и почему, не
  // написано нигде.
  useEffect(() => {
    if (!готово) {
      setИтог(null)
      setСчёт(null)
      return
    }
    let живо = true
    setСчёт('считаем')
    const таймер = setTimeout(() => {
      onPreview(запрос)
        .then((r) => {
          if (!живо) return
          setИтог(r)
          setСчёт(null)
        })
        .catch((e: any) => {
          if (!живо) return
          setИтог(null)
          // Своими словами, а не ответом сервера: «Not Found» человеку не
          // говорит ничего, а решение тут всегда одно — позвать нас.
          setСчёт(
            `Не удалось посчитать итог — программа не отвечает${
              e?.message ? ` (${e.message})` : ''
            }. Сохранить пока нельзя, сообщите нам.`,
          )
        })
    }, 350)
    return () => {
      живо = false
      clearTimeout(таймер)
    }
  }, [запрос, готово])

  // Почему нельзя сохранить — одной строкой, рядом с кнопкой
  const мешает = !готово
    ? 'Выберите товар и укажите количество'
    : счёт === 'считаем'
      ? 'Считаем итог…'
      : счёт
        ? счёт
        : итог?.problem
          ? итог.problem
          : null

  const менять = (ключ: string, что: Partial<BuilderTruck>) =>
    setTrucks((п) => п.map((t) => (t.key === ключ ? { ...t, ...что } : t)))

  const менять_строку = (
    грузовик: string,
    строка: string,
    что: Partial<BuilderItem>,
  ) =>
    setTrucks((п) =>
      п.map((t) =>
        t.key === грузовик
          ? { ...t, items: t.items.map((i) => (i.key === строка ? { ...i, ...что } : i)) }
          : t,
      ),
    )

  /**
   * Размножить грузовик. Номер грузовика и номер заказа сдвигаются на
   * единицу — именно так они и идут в поставке, — а состав повторяется.
   */
  const размножить = (ключ: string, сколько: number) =>
    setTrucks((п) => {
      const i = п.findIndex((t) => t.key === ключ)
      if (i < 0) return п
      const копии: BuilderTruck[] = []
      let предыдущий = п[i]
      for (let n = 0; n < сколько; n++) {
        предыдущий = {
          ...предыдущий,
          key: метка(),
          truck_no: следующий(предыдущий.truck_no),
          order_no: следующий(предыдущий.order_no),
          items: предыдущий.items.map((s) => ({ ...s, key: метка() })),
        }
        копии.push(предыдущий)
      }
      // Копии встают сразу за образцом: так видно, что из чего получилось
      return [...п.slice(0, i + 1), ...копии, ...п.slice(i + 1)]
    })

  async function сохранить() {
    setBusy(true)
    setError('')
    try {
      await onSave(запрос)
      onClose()
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось сохранить')
    } finally {
      setBusy(false)
    }
  }

  const первый_номер = итог?.trucks[0]?.first
  const последний = итог?.trucks[итог.trucks.length - 1]?.last

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal giant sb">
        <div className="tkw-head">
          <div className="tkw-titles">
            <div className="tkw-num">
              {order.trucks.length ? 'Пересобрать поставку' : 'Собрать поставку без файла'}
            </div>
            <div className="tkw-subject">{order.title}</div>
          </div>
          <button className="tkw-close" title="Закрыть окно" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && (
          <div className="error" style={{ margin: '10px 20px 0' }}>
            {error}
          </div>
        )}

        <div className="sb-intro">
          {order.trucks.length
            ? 'Правьте состав и сохраните: дерево будет заменено целиком на то, что здесь набрано.'
            : 'Заполните один грузовик и размножьте его кнопкой «ещё такой же» — состав в поставке обычно повторяется. Номера паллет программа посчитает сама.'}
        </div>

        <div className="sb-start">
          <div className="field">
            <label htmlFor="sb-start">Номер первой паллеты</label>
            <input
              id="sb-start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="T11P261"
              autoFocus
            />
            <span className="hint-inline">
              Один на всю поставку. Дальше — по порядку, не сбрасываясь на новом
              грузовике.
            </span>
          </div>
          {первый_номер && последний && (
            <div className="sb-range">
              <span className="sb-range-label">получится</span>
              <b>{первый_номер}</b> … <b>{последний}</b>
            </div>
          )}
        </div>

        <div className="sb-body">
          {trucks.map((t, н) => (
            <TruckCard
              key={t.key}
              truck={t}
              номер={н + 1}
              products={products}
              итог={итог?.trucks[н]}
              удалить={trucks.length > 1 ? () => setTrucks((п) => п.filter((x) => x.key !== t.key)) : undefined}
              менять={(что) => менять(t.key, что)}
              менять_строку={(строка, что) => менять_строку(t.key, строка, что)}
              добавить_строку={() =>
                менять(t.key, { items: [...t.items, пустая_строка()] })
              }
              убрать_строку={(строка) =>
                менять(t.key, { items: t.items.filter((i) => i.key !== строка) })
              }
              размножить={(сколько) => размножить(t.key, сколько)}
            />
          ))}

          <button
            className="btn sb-add-truck"
            onClick={() => setTrucks((п) => [...п, пустой_грузовик()])}
          >
            + Ещё грузовик
          </button>
        </div>

        <div className="sb-foot">
          <div className="sb-totals">
            <Сумма что="Грузовиков" сколько={итог?.trucks_count ?? 0} />
            <Сумма что="Позиций" сколько={итог?.items_count ?? 0} />
            <Сумма что="Паллет" сколько={итог?.pallets_count ?? 0} />
            <Сумма что="Штук" сколько={итог?.units_total ?? 0} />
          </div>
          <div className="sb-foot-actions">
            {мешает && <span className="sb-problem">⚠ {мешает}</span>}
            <button className="btn" onClick={onClose}>
              Отмена
            </button>
            <button
              className="btn primary"
              disabled={busy || !!мешает}
              title={мешает ?? undefined}
              onClick={() => void сохранить()}
            >
              {busy ? 'Сохраняем…' : 'Собрать поставку'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Сумма({ что, сколько }: { что: string; сколько: number }) {
  return (
    <div className="sb-total">
      <span className="sb-total-num">{сколько.toLocaleString('ru')}</span>
      <span className="sb-total-label">{что}</span>
    </div>
  )
}

function TruckCard({
  truck,
  номер,
  products,
  итог,
  менять,
  менять_строку,
  добавить_строку,
  убрать_строку,
  размножить,
  удалить,
}: {
  truck: BuilderTruck
  номер: number
  products: ClientProduct[]
  итог?: BuildPreview['trucks'][number]
  менять: (что: Partial<BuilderTruck>) => void
  менять_строку: (строка: string, что: Partial<BuilderItem>) => void
  добавить_строку: () => void
  убрать_строку: (строка: string) => void
  размножить: (сколько: number) => void
  удалить?: () => void
}) {
  const [сколько, setСколько] = useState('1')

  return (
    <div className="sb-truck">
      <div className="sb-truck-head">
        <span className="sb-truck-n">{номер}</span>
        <div className="field tiny">
          <label>Грузовик</label>
          <input
            value={truck.truck_no}
            onChange={(e) => менять({ truck_no: e.target.value })}
            placeholder="11"
          />
        </div>
        <div className="field tiny">
          <label>Номер заказа</label>
          <input
            value={truck.order_no}
            onChange={(e) => менять({ order_no: e.target.value })}
            placeholder="A-1806"
          />
        </div>
        <div className="field grow">
          <label>Куда</label>
          <input
            value={truck.address}
            onChange={(e) => менять({ address: e.target.value })}
            placeholder="необязательно"
          />
        </div>
        {итог && итог.pallets > 0 && (
          <div className="sb-truck-sum">
            {итог.pallets} {plural(итог.pallets, 'паллета', 'паллеты', 'паллет')} ·{' '}
            {итог.units.toLocaleString('ru')} шт
            {итог.first && (
              <span className="sb-truck-range">
                {итог.first} … {итог.last}
              </span>
            )}
          </div>
        )}
        {удалить && (
          <button className="link-btn danger" onClick={удалить} title="Убрать грузовик">
            убрать
          </button>
        )}
      </div>

      <table className="sb-items">
        <thead>
          <tr>
            <th>Товар</th>
            <th className="num">Штук в упаковке</th>
            <th className="num">Упаковок на паллете</th>
            <th className="num">Паллет</th>
            <th className="num">Штук</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {truck.items.map((i) => (
            <ItemRow
              key={i.key}
              item={i}
              products={products}
              менять={(что) => менять_строку(i.key, что)}
              убрать={truck.items.length > 1 ? () => убрать_строку(i.key) : undefined}
            />
          ))}
        </tbody>
      </table>

      <div className="sb-truck-foot">
        <button className="btn tiny" onClick={добавить_строку}>
          + товар
        </button>
        <div className="sb-repeat">
          <button
            className="btn tiny"
            onClick={() => размножить(Math.max(1, Math.min(50, Number(сколько) || 1)))}
            title="Повторить этот грузовик с тем же составом"
          >
            ещё такой же
          </button>
          <input
            className="sb-repeat-n"
            value={сколько}
            onChange={(e) => setСколько(e.target.value.replace(/\D/g, ''))}
            title="Сколько копий добавить"
          />
          <span className="hint-inline">шт — номера сдвинутся сами</span>
        </div>
      </div>
    </div>
  )
}

function ItemRow({
  item,
  products,
  менять,
  убрать,
}: {
  item: BuilderItem
  products: ClientProduct[]
  менять: (что: Partial<BuilderItem>) => void
  убрать?: () => void
}) {
  const товар = products.find((p) => p.gtin === item.gtin)
  // Коробов у товара бывает несколько — 12 и 24, — и это разные GTIN. Даём
  // выбрать из того, что записано в карточке, а не вводить число наугад.
  const коробы = (товар?.packs ?? []).filter((p) => p.units)

  const штук = (Number(item.per_pack) || 0) * (Number(item.packs_per_pallet) || 0)
  const всего = штук * (Number(item.pallets) || 0)

  return (
    <tr>
      <td>
        <select
          value={item.gtin}
          onChange={(e) => {
            const gtin = e.target.value
            const новый = products.find((p) => p.gtin === gtin)
            const один = (новый?.packs ?? []).filter((p) => p.units)
            менять({
              gtin,
              // Один короб — подставляем сразу: выбирать не из чего
              per_pack: один.length === 1 ? String(один[0].units) : '',
            })
          }}
        >
          <option value="">— выберите товар —</option>
          {products.map((p) => (
            <option key={p.id} value={p.gtin}>
              {p.name ?? p.gtin}
            </option>
          ))}
        </select>
        {item.gtin && <span className="sb-gtin">{item.gtin}</span>}
      </td>
      <td className="num">
        {коробы.length > 1 ? (
          <select
            value={item.per_pack}
            onChange={(e) => менять({ per_pack: e.target.value })}
          >
            <option value="">—</option>
            {коробы.map((к) => (
              <option key={к.gtin} value={String(к.units)}>
                {к.units}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="sb-n"
            value={item.per_pack}
            onChange={(e) => менять({ per_pack: e.target.value.replace(/\D/g, '') })}
            placeholder="12"
          />
        )}
      </td>
      <td className="num">
        <input
          className="sb-n"
          value={item.packs_per_pallet}
          onChange={(e) =>
            менять({ packs_per_pallet: e.target.value.replace(/\D/g, '') })
          }
          placeholder="120"
        />
      </td>
      <td className="num">
        <input
          className="sb-n"
          value={item.pallets}
          onChange={(e) => менять({ pallets: e.target.value.replace(/\D/g, '') })}
          placeholder="2"
        />
      </td>
      <td className="num sb-calc">{всего ? всего.toLocaleString('ru') : '—'}</td>
      <td className="num">
        {убрать && (
          <button className="link-btn danger" onClick={убрать} title="Убрать товар">
            ✕
          </button>
        )}
      </td>
    </tr>
  )
}
